import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, inject, NothingHere, SubTopBar, Title, toArray } from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
import { Input } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { AlertActionsStore } from '../AlertActions/AlertActionsStore';
import { DisplayAlertItem } from './DisplayAlertItem';
import { DisplayEventProfileStore } from './DisplayEventProfileStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { orderBy } from 'lodash';
import { BsCalendar } from '@react-icons/all-files/bs/BsCalendar';

export enum DisplayAlertActions {
    Create = 'create',
    Edit = 'edit',
    Clone = 'clone',
}

export interface IDisplayAlertsListProps {}

@autoinject
@observer
export class DisplayAlertsList extends React.Component<IDisplayAlertsListProps> {
    @action
    public async componentDidMount() {
        this.loadAllAsync();
    }

    public render() {
        const location = `alert-rules/create`;
        return (
            <div className="container-fluid">
                <Title>Alerts</Title>

                <SubTopBar>
                    <Input
                        type="text"
                        value={this.searchValue}
                        onChange={this.onSearchInputChange}
                        placeholder="Search Event Profiles..."
                    />

                    <div className="mx-2 border" />

                    <LinkContainer exact to={location} className="btn btn-primary">
                        <a>Create Alert</a>
                    </LinkContainer>
                </SubTopBar>

                <div className="row">
                    <div className="col-12 px-0">
                        <table className="table table-sm table-striped fixed-header">
                            <thead className="thead">
                                <tr>
                                    <th scope="col" className="no-wrap">
                                        Account
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Name
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Recipients
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Comment
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Description
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Filter
                                    </th>
                                    <th scope="col" className="no-wrap">
                                        Action
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {this.customerDisplayAlerts?.map(e => (
                                    <DisplayAlertItem key={e.displayEventProfileGuid} displayEvent={e} />
                                ))}
                            </tbody>
                        </table>

                        <NothingHere
                            empty={!this.customerDisplayAlerts.length}
                            icon={<BsCalendar />}
                            loading={this.loading}
                            label="No Alerts"
                            loadingMessage="Loading Alerts..."
                        />
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async loadAllAsync() {
        this.loading = true;
        try {
            await this.accountsStore.loadAsync();
            await this.displayEventProfileStore.loadAllAsync();
            await this.alertActionsStore.loadAllAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async onSearchInputChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.searchValue = target.value;
    }

    @computed
    private get displayAlerts() {
        let displayAlerts = toArray(this.displayEventProfileStore.displayEventProfiles);

        displayAlerts = displayAlerts.filter(d => d.alertActionGuid != null);

        if (this.searchValue) displayAlerts = displayAlerts.filter(d => deepStringFind(d, this.searchValue));

        displayAlerts = orderBy(displayAlerts, [a => a.account, a => a.name]);

        return displayAlerts;
    }

    @computed
    private get customerDisplayAlerts() {
        return this.displayAlerts.filter(d => d.account);
    }

    @observable
    private loading = false;
    @observable
    private searchValue: string = '';
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
    @inject
    private appStore!: AppStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
}
